import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Supabase";
import { INPUT_LIMITS, clampText } from "../lib/inputLimits";
import {
  STRONG_PASSWORD_RULES,
  meetsStrongPasswordPolicy,
} from "../lib/passwordPolicy";

const ResetPasswordPage = () => {
  const limit = (value, key) =>
    clampText(value ?? "", INPUT_LIMITS[key] ?? 255);

  const [checkingSession, setCheckingSession] = useState(true);
  const [user, setUser] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const passwordOk = useMemo(
    () => meetsStrongPasswordPolicy(password),
    [password]
  );
  const confirmOk = password && password === confirmPassword;
  const canSubmit = Boolean(
    user && passwordOk && confirmOk && !loading && !checkingSession
  );

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      try {
        const href =
          typeof window !== "undefined" ? window.location.href : undefined;

        // Ensure recovery tokens in the URL are exchanged for a session.
        if (href) {
          const hasRecoveryParams =
            href.includes("type=recovery") ||
            href.includes("token_hash=") ||
            href.includes("token=") ||
            href.includes("code=") ||
            href.includes("#access_token=");

          if (hasRecoveryParams) {
            const { error: exchangeError } =
              await supabase.auth.exchangeCodeForSession(href);
            if (exchangeError && isMounted) {
              setErr(
                exchangeError.message ||
                  "Your reset link is invalid or has expired. Please request a new one."
              );
            }
          }
        }

        const { data, error } = await supabase.auth.getUser();
        if (!isMounted) return;
        if (error) {
          setUser(null);
        } else {
          setUser(data?.user ?? null);
        }
      } catch (e) {
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) setCheckingSession(false);
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setUser(session?.user ?? null);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      setErr(null);
      setInfo(null);

      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;

      setInfo("Password updated. You can continue to the app.");
    } catch (e) {
      console.warn("Password update failed", e);
      setErr(e?.message || "Could not update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Password reset
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Set a new password
          </h1>
          <p className="text-xs text-slate-500">
            Use the link from your email to open this page. The link temporarily
            signs you in so you can choose a new password.
          </p>
        </div>

        {checkingSession ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Checking your session…
          </div>
        ) : (
          <form className="space-y-3" onSubmit={handleSubmit}>
            {!user && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Open this page using the password reset link we emailed you.
                Then set your new password here.
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">
                New password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(limit(e.target.value, "password"))}
                maxLength={INPUT_LIMITS.password}
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500"
                placeholder="12+ chars incl. symbol"
                disabled={loading || !user}
              />
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {STRONG_PASSWORD_RULES.map((rule) => {
                  const valid = rule.test(password);
                  return (
                    <li
                      key={rule.label}
                      className={valid ? "text-emerald-600" : "text-slate-400"}
                    >
                      {valid ? "✓" : "•"} {rule.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">
                Confirm new password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) =>
                  setConfirmPassword(limit(e.target.value, "password"))
                }
                maxLength={INPUT_LIMITS.password}
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500"
                placeholder="Re-enter new password"
                disabled={loading || !user}
              />
              {!confirmOk && confirmPassword.length > 0 && (
                <p className="mt-1 text-[11px] text-amber-700">
                  Passwords must match.
                </p>
              )}
            </div>

            {err && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {err}
              </div>
            )}

            {info && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {info}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className={[
                "flex w-full items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
                canSubmit
                  ? "bg-black text-white hover:bg-slate-800"
                  : "cursor-not-allowed bg-slate-200 text-slate-500",
              ].join(" ")}
            >
              {loading ? "Updating…" : "Update password"}
            </button>

            <div className="text-center text-[11px] text-slate-500">
              Need to go back?{" "}
              <a
                href="/"
                className="font-semibold text-slate-700 underline hover:text-slate-900"
              >
                Return to app
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
